import { Component, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { UserService } from '../../../layout/service/user.service';
import { ButtonModule } from 'primeng/button';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { TabsModule } from 'primeng/tabs';
import { InputTextModule } from 'primeng/inputtext';
import { CheckboxModule } from 'primeng/checkbox';
import { MessageService } from 'primeng/api';
import { TextareaModule } from 'primeng/textarea';
import { AutoFocusModule } from 'primeng/autofocus';
import { CountdownEvent, CountdownComponent, CountdownModule } from 'ngx-countdown';
import { ToastModule } from 'primeng/toast';

@Component({
    standalone: true,
    selector: 'app-sheet-answer',
    imports: [CommonModule, ButtonModule, FormsModule, TabsModule, InputTextModule, CheckboxModule, TextareaModule, AutoFocusModule, CountdownModule, ToastModule],
    providers: [MessageService],
    template: `
    <p-toast></p-toast>
    <div class="min-h-screen flex py-10 bg-gradient-to-br from-blue-50 via-white to-blue-100">
        <div class="w-full px-4 mx-auto flex flex-col md:flex-row gap-8">
            <div class="flex-1">
                <!-- Loading Indicator -->
                <div *ngIf="isLoading" class="flex justify-center items-center py-12">
                    <i class="pi pi-spin pi-spinner text-3xl text-blue-500"></i>
                    <span class="ml-3 text-blue-700 font-medium">Memuat data...</span>
                </div>

                <!-- Ucapan Selamat -->
                <div *ngIf="isSubmitted" class="flex flex-col items-center justify-center py-24">
                    <div class="bg-gradient-to-br from-blue-200 via-white to-green-100 rounded-3xl shadow-2xl px-8 py-12 max-w-xl w-full flex flex-col items-center border border-blue-100">
                        <i class="pi pi-check-circle text-green-500 text-6xl mb-4 animate-bounce"></i>
                        <h2 class="text-3xl md:text-4xl font-extrabold text-blue-900 mb-3 text-center drop-shadow">Selamat!</h2>
                        <p class="text-lg md:text-xl text-blue-800 mb-6 text-center font-medium">
                            Jawaban Anda telah berhasil dikumpulkan.<br>
                            Terima kasih atas partisipasi dan kerja keras Anda.<br>
                            Semoga sukses dalam OSCE!
                        </p>
                        <button
                            pButton
                            type="button"
                            label="Kembali ke Beranda"
                            icon="pi pi-home"
                            class="p-button-primary px-8 py-3 rounded-full shadow font-semibold"
                            (click)="goHome()"
                        ></button>
                    </div>
                </div>

                <!-- Tab dan Form -->
                <ng-container *ngIf="!isLoading && getCurrentOsceCase() && !isSubmitted">
                    <!-- Tombol Keluar Simulasi -->
                    <div class="flex justify-between items-center mb-4">
                        <!-- Status Simulasi -->
                        <div *ngIf="isSimulationPaused" class="flex items-center gap-2 bg-orange-100 text-orange-800 px-4 py-2 rounded-full border border-orange-200">
                            <i class="pi pi-pause text-orange-600"></i>
                            <span class="font-semibold">Simulasi Dijeda</span>
                        </div>
                        <div *ngIf="!isSimulationPaused" class="flex text-xl items-center gap-2 text-green-800" style="font-weight: 900;">
                            <i class="pi pi-clock text-green-800"></i>
                            <span>Simulasi Sedang Berjalan</span>
                        </div>

                        <div *ngIf="osceList.length > 1" class="text-[#337CD9] text-xl mb-2" style="font-weight: 900;">
                            OSCE {{ currentOsceIndex + 1 }} dari {{ osceList.length }}
                        </div>
                        
                        <!-- Tombol Kontrol -->
                        <div class="flex gap-2">
                            <button
                                pButton
                                type="button"
                                label="Jeda Simulasi"
                                icon="pi pi-pause"
                                class="p-button-warning px-4 py-2 shadow font-semibold"
                                (click)="pauseSimulation()"
                                *ngIf="!isSimulationPaused"
                            ></button>
                            <button
                                pButton
                                type="button"
                                label="Lanjutkan Simulasi"
                                icon="pi pi-play"
                                class="p-button-success px-4 py-2 shadow font-semibold"
                                (click)="resumeSimulation()"
                                *ngIf="isSimulationPaused"
                            ></button>
                            <button
                                pButton
                                type="button"
                                label="Keluar Simulasi"
                                icon="pi pi-sign-out"
                                class="p-button-danger px-4 py-2 shadow font-semibold"
                                (click)="exitSimulation()"
                            ></button>
                        </div>
                    </div>
                    
                    <!-- Pesan Jeda Simulasi -->
                    <div *ngIf="isSimulationPaused" class="mb-6 p-4 bg-orange-50 border-l-4 border-orange-400 rounded-r-lg">
                        <div class="flex items-center">
                            <i class="pi pi-info-circle text-orange-600 text-xl mr-3"></i>
                            <div>
                                <h4 class="text-orange-800 font-semibold">Simulasi Sedang Dijeda</h4>
                                <p class="text-orange-700 text-sm mt-1">
                                    Timer telah dihentikan sementara. Semua input dan tombol telah dinonaktifkan. 
                                    Klik "Lanjutkan Simulasi" untuk melanjutkan dari waktu tersisa.
                                </p>
                            </div>
                        </div>
                    </div>
                    
                    <div class="grid grid-cols-1 md:grid-cols-12 gap-6 mb-8">
                        <!-- Timer Card -->
                        <div class="col-span-12 md:col-span-3">
                            <div class="rounded-2xl flex flex-col items-center justify-center py-12 h-full min-h-[200px] min-w-[220px] w-full mx-auto"
                                style="height: 220px; background: linear-gradient(180deg, #5B91EE 0%, #4DC8DF 100%) !important;">
                                <!-- OSCE Indicator -->
                                <div class="text-white font-bold text-xl">
                                    <i class="pi pi-clock me-2"></i>
                                    Timer
                                    <span *ngIf="isSimulationPaused" class="text-orange-600 text-sm ml-2">
                                        <i class="pi pi-pause"></i> DIJEDA
                                    </span>
                                </div>
                                <div class="text-white text-base">Sisa waktu</div>
                                <countdown
                                    #cd
                                    *ngIf="getCurrentOsceCase()?.timer && !isSimulationPaused"
                                    [config]="timerConfig"
                                    (event)="onCountdownEvent($event)"
                                    class="text-7xl font-mono text-white font-bold "
                                ></countdown>
                                <!-- Static display when paused -->
                                <div *ngIf="isSimulationPaused" class="text-5xl font-mono text-orange-500">
                                    {{ formatPausedTime() }}
                                </div>
                            </div>
                        </div>
                        <!-- Kasus Card -->
                        <div class="col-span-12 md:col-span-9">
                            <div
                                class="relative rounded-2xl shadow-lg border-2 border-blue-200 bg-white p-10 flex flex-col min-h-[320px] h-full transition-all duration-300 group overflow-hidden"
                                style="height: 220px;">
                                <!-- Decorative Gradient Accent -->
                                <div class="absolute -top-10 -left-10 w-40 h-40 bg-gradient-to-br from-blue-200 via-blue-100 to-transparent rounded-full opacity-40 pointer-events-none z-0"></div>
                                <div class="absolute -bottom-10 -right-10 w-32 h-32 bg-gradient-to-tr from-yellow-100 via-blue-50 to-transparent rounded-full opacity-30 pointer-events-none z-0"></div>
                                <!-- Card content -->
                                <div class="flex flex-wrap gap-3 z-10 relative">
                                    <span class="inline-flex items-center gap-1 bg-blue-100 text-blue-700 text-base px-4 py-1 rounded font-medium shadow border border-blue-200">
                                        <i class="pi pi-clock"></i> {{ getCurrentOsceCase()?.timer }} min
                                    </span>
                                    <span class="inline-flex items-center gap-1 bg-yellow-100 text-yellow-700 text-base px-4 py-1 rounded font-medium shadow border border-yellow-200">
                                        <i class="pi pi-calendar"></i> {{ getCurrentOsceCase()?.created_at | date:'dd MMM yyyy' }}
                                    </span>
                                </div>
                                <div class="mt-4 z-10 relative">
                                    <!-- OSCE Name Display -->
                                    <div *ngIf="getCurrentOsce()?.NAMA_OSCE" class="mb-3">
                                        <span class="inline-flex items-center gap-1 bg-green-100 text-green-700 text-sm px-3 py-1 rounded-full font-medium shadow border border-green-200">
                                            <i class="pi pi-bookmark"></i> {{ getCurrentOsce()?.NAMA_OSCE }}
                                        </span>
                                    </div>
                                    <div class="text-gray-600 text-lg md:text-base leading-relaxed break-words overflow-hidden max-h-[200px] overflow-y-auto" [innerHTML]="getCurrentOsceCase()?.description"></div>
                                </div>
                            </div>
                            <div class="mt-4 flex justify-end gap-2">
                                <button
                                    *ngIf="!rmeShowing"
                                    pButton
                                    type="button"
                                    label="Masuk RME Pasien"
                                    class="p-button-primary"
                                    (click)="setRMEVisibility(true)"
                                ></button>
                            </div>
                        </div>
                    </div>
                    <div class="" *ngIf="rmeShowing">
                        <p-tabs [value]="activeTab.toString()" class="custom-tabs" (onChange)="onTabChange($event)">
                            <p-tablist>
                                <p-tab value="0">
                                    <span class="custom-tab custom-tab-blue">Pemeriksaan</span>
                                </p-tab>
                                <p-tab value="1" [disabled]="!isTabComplete(0)">
                                    <span class="custom-tab custom-tab-green">Diagnosis</span>
                                </p-tab>
                                <p-tab value="2" [disabled]="!isTabComplete(0) || !isTabComplete(1)">
                                    <span class="custom-tab custom-tab-yellow">Terapi</span>
                                </p-tab>
                            </p-tablist>
                            <p-tabpanels>
                                <p-tabpanel value="0">
                                        <ng-container *ngIf="sheetDetailsList.length === 0">
                                            <!-- Tampilkan pemeriksaan jika ada data -->
                                            <div class="card" *ngIf="groupedSheetDetails && groupedSheetDetailKeys().length > 0">
                                                <!-- Search input -->
                                                <p class="text-gray-700 mb-4">Pilih pemeriksaan yang Anda ajukan (minimal 1)</p>
                                                <div class="mb-6 flex items-center gap-2">
                                                    <i class="pi pi-search text-gray-400 absolute ml-3"></i>
                                                    <input
                                                        pInputText
                                                        type="text"
                                                        placeholder="Cari detail pemeriksaan"
                                                        class="w-full pl-10 p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                        [(ngModel)]="searchSheetDetail"
                                                        (ngModelChange)="applySearchSheetDetail()"
                                                    />
                                                </div>
                                                <!-- Tampilkan pesan jika tidak ada hasil filter -->
                                                <div *ngIf="isFilteredSheetDetailEmpty()" class="text-center py-8">
                                                    <!-- Pesan ketika kosong karena filter OSCE -->
                                                    <div *ngIf="isNoDataForCurrentOsce()" class="text-orange-600">
                                                        <i class="pi pi-info-circle text-4xl mb-3"></i>
                                                        <div class="text-lg font-medium mb-2">Tidak ada pemeriksaan untuk OSCE ini</div>
                                                        <div class="text-sm">Pemeriksaan belum dikonfigurasi untuk OSCE yang sedang aktif.</div>
                                                    </div>
                                                    <!-- Pesan ketika kosong karena pencarian -->
                                                    <div *ngIf="isEmptyDueToSearch()" class="text-gray-500">
                                                        <i class="pi pi-search text-4xl mb-3"></i>
                                                        <div class="text-lg font-medium mb-2">Tidak ada pemeriksaan ditemukan</div>
                                                        <div class="text-sm">Coba kata kunci lain atau hapus filter pencarian.</div>
                                                    </div>
                                                    <!-- Pesan umum ketika tidak ada data sama sekali -->
                                                    <div *ngIf="!isNoDataForCurrentOsce() && !isEmptyDueToSearch()" class="text-gray-500">
                                                        <i class="pi pi-inbox text-4xl mb-3"></i>
                                                        <div class="text-lg font-medium mb-2">Belum ada pemeriksaan tersedia</div>
                                                        <div class="text-sm">Silakan hubungi administrator untuk menambah data pemeriksaan.</div>
                                                    </div>
                                                </div>
                                                <!-- Loop group hanya jika hasil filter group tidak kosong -->
                                                <div *ngFor="let group of groupedSheetDetailKeys()" class="mb-8" >
                                                    <ng-container *ngIf="filteredGroupedSheetDetails[group]?.length > 0">
                                                        <h3 class="font-bold text-gray-800 text-lg mb-4 uppercase tracking-wide">
                                                            {{ group }}
                                                        </h3>
                                                        <div class="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
                                                            <div
                                                                *ngFor="let detail of filteredGroupedSheetDetails[group]; let i = index"
                                                                class="relative rounded-2xl p-6 shadow-md hover:shadow-xl transition-all duration-200 cursor-pointer border-2 flex flex-col items-center justify-center gap-3 min-h-[140px]"
                                                                [ngClass]="getCardColor(i)"
                                                                [class.opacity-50]="(isMaxChecked && !checkedSheetDetails[detail.id]) || isSimulationPaused"
                                                                [class.pointer-events-none]="(isMaxChecked && !checkedSheetDetails[detail.id]) || isSimulationPaused"
                                                                [class.ring-4]="checkedSheetDetails[detail.id]"
                                                                [class.ring-blue-500]="checkedSheetDetails[detail.id]"
                                                                (click)="toggleCheckbox(detail)"
                                                            >
                                                                <!-- Checkbox di pojok kanan atas -->
                                                                <div class="absolute top-2 right-2">
                                                                    <p-checkbox
                                                                        [(ngModel)]="checkedSheetDetails[detail.id]"
                                                                        binary="true"
                                                                        [disabled]="(isMaxChecked && !checkedSheetDetails[detail.id]) || isSimulationPaused"
                                                                        (onChange)="onInterpretasiChange(detail, checkedSheetDetails[detail.id])"
                                                                        (click)="$event.stopPropagation()"
                                                                    ></p-checkbox>
                                                                </div>
                                                                <!-- Icon -->
                                                                <div class="text-5xl" *ngIf="detail.icon">
                                                                    <img [src]="detail.icon" alt="{{ detail.name }}" class="w-16 h-16 object-contain" />
                                                                </div>
                                                                <div class="text-5xl text-gray-600" *ngIf="!detail.icon">
                                                                    <i class="pi pi-file" style="font-size: 3rem;"></i>
                                                                </div>
                                                                <!-- Nama pemeriksaan -->
                                                                <span class="font-semibold text-gray-800 text-center text-sm leading-tight">{{ detail.name }}</span>
                                                            </div>
                                                        </div>
                                                    </ng-container>
                                                </div>
                                            </div>
                                            
                                            <!-- Pesan ketika tidak ada data pemeriksaan sama sekali -->
                                            <div *ngIf="!groupedSheetDetails || groupedSheetDetailKeys().length === 0" class="text-center py-12">
                                                <div class="text-orange-600">
                                                    <i class="pi pi-exclamation-triangle text-5xl mb-4"></i>
                                                    <div class="text-xl font-bold mb-2">Tidak ada pemeriksaan tersedia</div>
                                                    <div class="text-base mb-4">Belum ada pemeriksaan yang dikonfigurasi untuk OSCE ini.</div>
                                                    <div class="text-sm text-gray-600">Silakan hubungi administrator untuk menambahkan data pemeriksaan.</div>
                                                </div>
                                            </div>
                                            
                                            <div class="flex justify-end mt-8">
                                                <button
                                                    *ngIf="hasCheckedSheetDetails() && !isSimulationPaused"
                                                    pButton
                                                    type="button"
                                                    label="Lanjutkan"
                                                    icon="pi pi-arrow-right"
                                                    class="p-button-primary px-6 py-2 rounded-full shadow font-semibold"
                                                    (click)="goToNextTab()"
                                                ></button>
                                            </div>
                                            <div *ngIf="isPreparingContent" class="fixed inset-0 flex items-center justify-center bg-black bg-opacity-30 z-50">
                                                <div class="bg-white rounded-xl shadow-lg px-8 py-6 flex flex-col items-center gap-3">
                                                    <i class="pi pi-spin pi-spinner text-3xl text-blue-500"></i>
                                                    <span class="text-blue-700 font-medium">Sedang menyusun konten...</span>
                                                </div>
                                            </div>
                                        </ng-container>
                                        <!-- Render SheetDetailsList if loaded -->
                                        <ng-container *ngIf="sheetDetailsList.length > 0">
                                            <div class="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                                                <div class="flex justify-between items-center">
                                                    <p class="text-green-800 font-medium">
                                                        <i class="pi pi-check-circle mr-2"></i>
                                                        Pemeriksaan telah dipilih. Anda dapat melanjutkan ke diagnosis atau kembali mengubah pilihan.
                                                    </p>
                                                    <!--
                                                    <button
                                                        pButton
                                                        type="button"
                                                        label="Pilih Ulang Pemeriksaan"
                                                        icon="pi pi-edit"
                                                        class="p-button-secondary p-button-outlined p-button-sm px-3 py-1 rounded-full shadow font-semibold"
                                                        (click)="resetPemeriksaanSelection()"
                                                        [disabled]="isSimulationPaused"
                                                    ></button>
                                                    -->
                                                </div>
                                            </div>
                                            <div class="mb-6" *ngFor="let group of groupedSheetDetailsTableResult">
                                                <div class="font-bold text-blue-900 mb-2 flex items-center gap-2">
                                                    {{ group.sheet_name }}
                                                </div>
                                                <div *ngIf="group.header; else htmlContentGroup">
                                                    <table class="min-w-full border border-blue-400 rounded mb-4" style="border-collapse: collapse;">
                                                        <thead>
                                                            <tr>
                                                                <th *ngFor="let h of parseHeader(group.header)" class="px-4 py-2 border border-blue-400 bg-blue-50 text-blue-900 text-left">{{ h }}</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            <tr *ngFor="let row of getTableRows(group)">
                                                                <ng-container *ngIf="row.length > 0; else emptyRow">
                                                                    <td *ngFor="let cell of row; let i = index" class="px-4 py-2 border border-blue-400">
                                                                        <span [innerHTML]="cell && cell.trim() !== '' ? cell : '-'"></span>
                                                                    </td>
                                                                    <td *ngFor="let h of [].constructor(parseHeader(group.header).length - row.length)" class="px-4 py-2 border border-blue-400">-</td>
                                                                </ng-container>
                                                                <ng-template #emptyRow>
                                                                    <td *ngFor="let h of parseHeader(group.header)" class="px-4 py-2 border border-blue-400">-</td>
                                                                </ng-template>
                                                            </tr>
                                                        </tbody>
                                                    </table>
                                                </div>
                                                <ng-template #htmlContentGroup>
                                                    <div *ngFor="let detail of group.details" class="prose max-w-none mt-4" [innerHTML]="detail.content"></div>
                                                </ng-template>
                                                <div *ngIf="group.interpretasi == 1" class="mt-4">
                                                    <div class="mb-3">
                                                        <label class="block mb-2 font-semibold text-blue-900">Interpretasi untuk pemeriksaan:</label>
                                                        <div class="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-3">
                                                            <div class="flex flex-wrap gap-2">
                                                                <span *ngFor="let detail of getDetailsForInterpretation(group.details)" 
                                                                      class="inline-flex items-center px-3 py-1 rounded-full text-sm bg-blue-100 text-blue-800 border border-blue-300">
                                                                    <i class="pi pi-check-circle mr-1"></i>
                                                                    {{ detail.name }}
                                                                </span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <textarea
                                                        rows="5"
                                                        cols="30"
                                                        pTextarea
                                                        [(ngModel)]="interpretasiForm[group.sheet_name + '||' + group.header]"
                                                        (ngModelChange)="saveInterpretasiData()"
                                                        class="w-full"
                                                        placeholder="Tulis interpretasi anda berdasarkan hasil pemeriksaan di atas..."
                                                        [disabled]="isSimulationPaused"
                                                    ></textarea>
                                                </div>
                                            </div>
                                            <div class="flex justify-end mt-8">
                                                <button
                                                    *ngIf="!isSimulationPaused && isCurrentTabComplete()"
                                                    pButton
                                                    type="button"
                                                    label="Lanjutkan ke Diagnosis"
                                                    icon="pi pi-arrow-right"
                                                    class="p-button-primary px-6 py-2 rounded-full shadow font-semibold"
                                                    (click)="goToNextTabAfterSheetDetails()"
                                                ></button>
                                            </div>
                                        </ng-container>
                                    </p-tabpanel>
                                <p-tabpanel value="1">
                                    <div class="mb-6 card">
                                        <p class="font-semibold text-blue-900 mb-2">Tuliskan diagnosa yang anda tetapkan <span class="text-red-500">*</span></p>
                                        <div class="mb-4">
                                            <label class="block mb-1 font-medium">Diagnosa Utama <span class="text-red-500">*</span></label>
                                            <textarea
                                                rows="2"
                                                pTextarea
                                                [(ngModel)]="diagnosaForm.diagnosaUtama"
                                                (ngModelChange)="saveDiagnosisData()"
                                                class="w-full"
                                                placeholder="Diagnosa utama (wajib diisi)"
                                                [disabled]="isSimulationPaused"></textarea>
                                        </div>
                                        <div class="mb-4">
                                            <label class="block mb-1 font-medium">Diagnosa Banding 1 <span class="text-red-500">*</span></label>
                                            <textarea
                                                rows="2"
                                                pTextarea
                                                [(ngModel)]="diagnosaForm.diagnosaBanding1"
                                                (ngModelChange)="saveDiagnosisData()"
                                                class="w-full"
                                                placeholder="Diagnosa banding 1 (wajib diisi)"
                                                [disabled]="isSimulationPaused"></textarea>
                                        </div>
                                        <div class="mb-4">
                                            <label class="block mb-1 font-medium">Diagnosa Banding 2 <span class="text-red-500">*</span></label>
                                            <textarea
                                                rows="2"
                                                pTextarea
                                                [(ngModel)]="diagnosaForm.diagnosaBanding2"
                                                (ngModelChange)="saveDiagnosisData()"
                                                class="w-full"
                                                placeholder="Diagnosa banding 2 (wajib diisi)"
                                                [disabled]="isSimulationPaused"></textarea>
                                        </div>
                                    </div>
                                    <div class="flex justify-end mt-8">
                                        <button
                                            *ngIf="isCurrentTabComplete() && !isSimulationPaused"
                                            pButton
                                            type="button"
                                            label="Lanjutkan ke Terapi"
                                            icon="pi pi-arrow-right"
                                            class="p-button-primary px-6 py-2 rounded-full shadow font-semibold"
                                            (click)="goToNextTab()"
                                        ></button>
                                    </div>
                                </p-tabpanel>
                                <p-tabpanel value="2">
                                    <div class="mb-6 card">
                                        <p class="font-semibold text-blue-900 mb-2">Tuliskan resep yang anda berikan kepada pasien</p>
                                        <div *ngFor="let resep of resepList; let i = index" class="mb-3 flex items-start gap-2">
                                            <label class="block mt-2 font-semibold text-blue-900 min-w-[2rem]">R/</label>
                                            <textarea
                                                rows="2"
                                                pTextarea
                                                [(ngModel)]="resepList[i]"
                                                (ngModelChange)="saveResepData()"
                                                class="w-full"
                                                [pAutoFocus]="true"
                                                placeholder="Tulis resep"
                                                [disabled]="isSimulationPaused"></textarea>
                                            <button
                                                *ngIf="resepList.length > 1"
                                                type="button"
                                                pButton
                                                icon="pi pi-trash"
                                                class="p-button-danger p-button-rounded p-button-sm mt-2"
                                                (click)="removeResep(i)"
                                                [disabled]="isSimulationPaused">
                                            </button>
                                        </div>
                                        <button
                                            type="button"
                                            pButton
                                            icon="pi pi-plus"
                                            label="Tambah Resep"
                                            class="p-button-success p-button-sm"
                                            (click)="addResep()"
                                            [disabled]="isSimulationPaused">
                                        </button>
                                    </div>
                                    <div class="flex justify-end mt-8">
                                        <button
                                            *ngIf="isCurrentTabComplete() && !isSimulationPaused"
                                            pButton
                                            type="button"
                                            label="Kumpulkan"
                                            icon="pi pi-send"
                                            class="p-button-primary px-6 py-2 rounded-full shadow font-semibold"
                                            (click)="onSubmit()"
                                        ></button>
                                    </div>
                                </p-tabpanel>
                            </p-tabpanels>
                        </p-tabs>
                    </div>
                </ng-container>
                <div *ngIf="!getCurrentOsceCase() && !isSubmitted" class="text-center text-gray-400 py-16">
                    <i class="pi pi-search text-4xl md:text-5xl mb-3 animate-bounce"></i>
                    <div class="text-base md:text-lg font-medium">Tidak ada kasus yang sesuai dengan OSCE ini.</div>
                    <div class="text-sm text-gray-500 mt-2">Silakan hubungi administrator untuk menambah kasus yang sesuai.</div>
                </div>
            </div>
        </div>
    </div>
    <!-- Floating Submit Button -->
    <!-- Hapus floating button Kumpulkan di luar tab -->
    `,
    styles: [`
        .p-tablist-tab-list {
            background : transparent !important;
        }
        .p-button-primary {
            background: linear-gradient(90deg, #3b82f6 0%, #2563eb 100%) !important;
            color: #fff !important;
            border: none !important;
            border-radius: 0.75rem !important;
            box-shadow: 0 4px 16px 0 rgba(59,130,246,0.10) !important;
        }
        .p-button-primary:hover {
            background: linear-gradient(90deg, #2563eb 0%, #1e40af 100%) !important;
            transform: scale(1.05);
        }
        .p-button-danger {
            background: #dc2626 !important;
            color: #fff !important;
            border: 2px solid #dc2626 !important;
            border-radius: 0.75rem !important;
            box-shadow: 0 4px 16px 0 rgba(220,38,38,0.10) !important;
        }
        .p-button-danger:hover {
            background: #b91c1c !important;
            border-color: #b91c1c !important;
            transform: scale(1.05);
        }
        .p-button-danger.p-button-outlined {
            background: transparent !important;
            color: #dc2626 !important;
            border: 2px solid #dc2626 !important;
        }
        .p-button-danger.p-button-outlined:hover {
            background: #dc2626 !important;
            color: #fff !important;
            border-color: #dc2626 !important;
            transform: scale(1.05);
        }
        .p-button-warning {
            background: #f59e0b !important;
            color: #fff !important;
            border: 2px solid #f59e0b !important;
            border-radius: 0.75rem !important;
            box-shadow: 0 4px 16px 0 rgba(245,158,11,0.10) !important;
        }
        .p-button-warning:hover {
            background: #d97706 !important;
            border-color: #d97706 !important;
            transform: scale(1.05);
        }
        .p-button-warning.p-button-outlined {
            background: transparent !important;
            color: #f59e0b !important;
            border: 2px solid #f59e0b !important;
        }
        .p-button-warning.p-button-outlined:hover {
            background: #f59e0b !important;
            color: #fff !important;
            border-color: #f59e0b !important;
            transform: scale(1.05);
        }
        .p-button-success {
            background: #10b981 !important;
            color: #fff !important;
            border: 2px solid #10b981 !important;
            border-radius: 0.75rem !important;
            box-shadow: 0 4px 16px 0 rgba(16,185,129,0.10) !important;
        }
        .p-button-success:hover {
            background: #059669 !important;
            border-color: #059669 !important;
            transform: scale(1.05);
        }
        .p-button-success.p-button-outlined {
            background: transparent !important;
            color: #10b981 !important;
            border: 2px solid #10b981 !important;
        }
        .p-button-success.p-button-outlined:hover {
            background: #10b981 !important;
            color: #fff !important;
            border-color: #10b981 !important;
            transform: scale(1.05);
        }
        .p-button-secondary {
            background: #6b7280 !important;
            color: #fff !important;
            border: 2px solid #6b7280 !important;
            border-radius: 0.75rem !important;
            box-shadow: 0 4px 16px 0 rgba(107,114,128,0.10) !important;
        }
        .p-button-secondary:hover {
            background: #4b5563 !important;
            border-color: #4b5563 !important;
            transform: scale(1.05);
        }
        .p-button-secondary.p-button-outlined {
            background: transparent !important;
            color: #6b7280 !important;
            border: 2px solid #6b7280 !important;
        }
        .p-button-secondary.p-button-outlined:hover {
            background: #6b7280 !important;
            color: #fff !important;
            border-color: #6b7280 !important;
            transform: scale(1.05);
        }
        .p-button:focus {
            outline: 2px solid #2563eb !important;
            outline-offset: 2px !important;
        }
        .group:hover .group-hover\\:text-blue-600 {
            color: #2563eb !important;
        }
        .shadow-2xl {
            box-shadow: 0 8px 32px 0 rgba(59,130,246,0.13), 0 1.5px 8px 0 rgba(0,0,0,0.04) !important;
        }
        .drop-shadow-sm {
            filter: drop-shadow(0 1px 2px rgba(59,130,246,0.07));
        }
        .p-tab {
            padding: 0 !important;
        }
        .custom-tabs .p-tablist {
            display: flex;
            margin-bottom: 0;
            border: none;
            overflow: visible;
        }
        .custom-tab {
            display: inline-block;
            min-width: 120px;
            text-align: center;
            font-weight: 600;
            font-size: 1.1rem;
            padding: 0.7rem 1.5rem;
            border-radius: 20px;
            margin-right: 0.5rem;
            box-shadow: 0 2px 8px 0 rgba(0,0,0,0.04);
            transition: background 0.2s, color 0.2s;
            cursor: pointer;
        }
            

        .custom-tabs .p-tab.p-highlight .custom-tab-blue {
            border: rgb(5, 121, 174) 2px solid !important;
            color: #fff !important;
        }
        .custom-tabs .p-tab.p-highlight .custom-tab-green {
            border: #22c55e 2px solid !important;
            color: #fff !important;
        }
        .custom-tabs .p-tab.p-highlight .custom-tab-yellow {
            border: #fde047 2px solid !important;
            color: #b45309 !important;
        }
        .custom-tabs .p-tab.p-tab-active .custom-tab-blue {
            background: rgb(5, 121, 174) !important;
            opacity: 1 !important;
            color: #fff !important;
        }
        .custom-tabs .p-tab.p-tab-active .custom-tab-green {
            background: #22c55e !important;
            opacity: 1 !important;
            color: #fff !important;
        }
        .custom-tabs .p-tab.p-tab-active .custom-tab-yellow {
            background: #fde047 !important;
            opacity: 1 !important;
            color: #b45309 !important;
        }
        .custom-tab-blue {
            border: #3C7CE9 2px solid;
            background: #3C7CE90D;
            color: #3C7CE9;
        }
        .custom-tab-green {
            border: #50C762 2px solid;
            background: #50C7620D;
            color: #50C762;
        }
        .custom-tab-yellow {
            border: #EFB700 2px solid;
            background: #EFB7000D;
            color: #EFB700;
        }
        .custom-tabs .p-tab.p-highlight .custom-tab-blue,
        .custom-tabs .p-tab.p-highlight .custom-tab-green,
        .custom-tabs .p-tab.p-highlight .custom-tab-yellow {
            box-shadow: 0 4px 16px 0 rgba(52,168,83,0.10);
            font-weight: 700;
            color: #111;
        }
        .custom-tabs .p-tab {
            background: transparent !important;
            border: none !important;
            box-shadow: none !important;
        }
        .custom-tabs .p-tab:not(.p-highlight) .custom-tab {
            opacity: 1;
        }
        .custom-tabs .p-tab:not(.p-highlight) .custom-tab:hover {
            opacity: 1;
        }
        .fixed {
            position: fixed !important;
        }
        .bottom-6 {
            bottom: 1.5rem !important;
        }
        .right-6 {
            right: 1.5rem !important;
        }
        .rounded-full {
            border-radius: 9999px !important;
        }
        .z-50 {
            z-index: 50 !important;
        }
        @media (max-width: 600px) {
            .bottom-6 {
                bottom: 1rem !important;
            }
            .right-6 {
                right: 1rem !important;
            }
            .px-6 {
                padding-left: 1rem !important;
                padding-right: 1rem !important;
            }
            .py-3 {
                padding-top: 0.75rem !important;
                padding-bottom: 0.75rem !important;
            }
        }
        @media (max-width: 768px) {
            .responsive-rounded {
                border-radius: 1rem !important;
            }
            .responsive-p-10 {
                padding: 1.5rem !important;
            }
            .responsive-mb-8 {
                margin-bottom: 1.5rem !important;
            }
            .responsive-min-h-260 {
                min-height: 180px !important;
            }
            .responsive-text-3xl {
                font-size: 1.5rem !important;
            }
            .responsive-text-lg {
                font-size: 1rem !important;
            }
            .responsive-text-base {
                font-size: 0.95rem !important;
            }
            .responsive-px-4 {
                padding-left: 0.75rem !important;
                padding-right: 0.75rem !important;
            }
            .responsive-py-1 {
                padding-top: 0.25rem !important;
                padding-bottom: 0.25rem !important;
            }
        }
        @media (max-width: 480px) {
            .responsive-p-10 {
                padding: 1rem !important;
            }
            .responsive-mb-8 {
                margin-bottom: 1rem !important;
            }
            .responsive-text-2xl, .responsive-text-3xl {
                font-size: 1.1rem !important;
            }
            .responsive-min-h-48 {
                min-height: 24px !important;
            }
        }
    `]
})
export class SheetAnswer implements OnInit {
    cases: any[] = [];
    filteredCases: any[] = [];
    user: any = null;
    userSections: string[] = [];
    searchText: string = '';
    private caseId: any;
    private osceIndexKey: string = '';
    private submittedLockKey: string = '';
    groupedSheetDetails: { [sheetName: string]: any[] } = {};
    checkedSheetDetails: { [id: string]: boolean } = {};
    checkedSheetDetailsNames: { [id: string]: string } = {}; // Menyimpan nama pemeriksaan yang dipilih
    searchSheetDetail: string = '';
    filteredGroupedSheetDetails: { [sheetName: string]: any[] } = {};
    isMaxChecked: boolean = false;
    isLoading: boolean = false;
    activeTab: number = 0;
    rmeShowing: boolean = false;
    sheetDetailsList: any[] = [];
    groupedSheetDetailsTableResult: any[] = [];
    isPreparingContent: boolean = false;
    currentParticipant: any = null;
    packages: any[] = [];
    osceList: any[] = [];
    currentOsceIndex: number = 0;
    isSimulationPaused: boolean = false;

    // Tambahkan property untuk menyimpan interpretasi user
    interpretasiForm: { [sheetDetailId: string]: string } = {};
    diagnosaForm = {
        diagnosaUtama: '',
        diagnosaBanding1: '',
        diagnosaBanding2: ''
    };
    resepList: string[] = ['']; // Tambahkan property untuk daftar resep
    isSubmitted: boolean = false;

    // Getter for checking if resep has valid input
    get hasValidResep(): boolean {
        return this.resepList.some(r => r.trim());
    }

    // Tambahkan property untuk countdown
    countdownLeftTime: number | null = null;
    countdownKey: string = '';
    countdownEndTime: number | null = null; // waktu akhir epoch seconds
    timerConfig = { leftTime: 0, format: 'mm:ss' };

    @ViewChild(CountdownComponent) countdownRef?: CountdownComponent;

    // Method to update timer config
    updateTimerConfig(): void {
        const currentCase = this.getCurrentOsceCase();
        if (currentCase?.timer) {
            this.timerConfig.leftTime = this.getTimerSeconds(currentCase.timer);
        }
    }

    constructor(
        private userService: UserService,
        private route: ActivatedRoute,
        private router: Router,
        private messageService: MessageService,
    ) { }

    ngOnInit(): void {
        // Initialize keys
        this.route.paramMap.subscribe(params => {
            const newCaseId = params.get('id');
            const previousCaseId = localStorage.getItem('current_case_id');
            
            // Jika case ID berbeda, reset OSCE index
            if (previousCaseId && previousCaseId !== newCaseId) {
                localStorage.removeItem(`current_osce_index_${previousCaseId}`);
            }
            
            this.caseId = newCaseId;
            localStorage.setItem('current_case_id', this.caseId || '');
            this.countdownKey = `countdown_endTime_${this.caseId}`;
            this.osceIndexKey = `current_osce_index_${this.caseId}`;
            this.submittedLockKey = `osce_is_submitted_${this.caseId}`;
            
            // Check if already submitted
            if (localStorage.getItem(this.submittedLockKey) === '1') {
                this.router.navigate(['/app/osce/sheet/history']);
                return;
            }
            
            // Load saved OSCE index
            const savedOsceIndex = localStorage.getItem(this.osceIndexKey);
            if (savedOsceIndex) {
                this.currentOsceIndex = parseInt(savedOsceIndex, 10) || 0;
            }
            
            // Check if simulation was paused
            const pausedTimeLeft = localStorage.getItem(`paused_time_left_${this.caseId}`);
            if (pausedTimeLeft) {
                this.isSimulationPaused = true;
            }
            
            this.initApi();
        });

        // Load user data
        const userStr = localStorage.getItem('user');
        if (userStr) {
            this.user = JSON.parse(userStr);
            try {
                this.userSections = Array.isArray(this.user.LIST_SECTION)
                    ? this.user.LIST_SECTION
                    : JSON.parse(this.user.LIST_SECTION || '[]');
            } catch {
                this.userSections = [];
            }
        }

        // Restore checked sheet details but always start from tab 0
        const checkedStr = localStorage.getItem('checkedSheetDetails');
        if (checkedStr) {
            try {
                this.checkedSheetDetails = JSON.parse(checkedStr);
                const checkedIds = Object.keys(this.checkedSheetDetails).filter(id => this.checkedSheetDetails[id]);
                
                if (checkedIds.length > 0) {
                    // Restore sheetDetailsList from localStorage first
                    const sheetDetailsStr = localStorage.getItem(`sheetDetailsList_${this.caseId}_${this.currentOsceIndex}`);
                    if (sheetDetailsStr) {
                        try {
                            this.sheetDetailsList = JSON.parse(sheetDetailsStr);
                            this.groupedSheetDetailsTableResult = this.buildGroupedSheetDetailsTable();
                        } catch {
                            this.sheetDetailsList = [];
                        }
                    }
                    
                    // Load sheet details but stay on pemeriksaan tab
                    this.fetchSheetDetailsListForRestore(checkedIds);
                }
            } catch {
                this.checkedSheetDetails = {};
            }
        }

        // Restore checked sheet details names
        const checkedNamesStr = localStorage.getItem('checkedSheetDetailsNames');
        if (checkedNamesStr) {
            try {
                this.checkedSheetDetailsNames = JSON.parse(checkedNamesStr);
            } catch {
                this.checkedSheetDetailsNames = {};
            }
        }

        // Always start from pemeriksaan tab (tab 0)
        // this.activeTab = 0; // Commented out to allow restoration

        // Restore form data
        this.restoreFormData();
        
        // Set activeTab based on available data if not restored
        if (!localStorage.getItem(`activeTab_${this.caseId}_${this.currentOsceIndex}`)) {
            this.activeTab = 0;
            if (this.diagnosaForm.diagnosaUtama || this.diagnosaForm.diagnosaBanding1 || this.diagnosaForm.diagnosaBanding2) {
                this.activeTab = 1; // Diagnosis tab
            }
            if (this.resepList.some(r => r.trim())) {
                this.activeTab = 2; // Terapi tab
            }
        }
        
        // Ensure the search filter is applied for the first tab
        this.applySearchSheetDetail();
        
        // Debug log to track initialization

    }
    
    setRMEVisibility(isVisible: boolean): void {
        this.rmeShowing = isVisible;
        localStorage.setItem(`rme_showing_${this.caseId}_${this.currentOsceIndex}`, isVisible.toString());
    }

    // Method to restore form data from localStorage
    restoreFormData(): void {
        // Restore diagnosis form
        const diagnosisStr = localStorage.getItem(`diagnosis_${this.caseId}_${this.currentOsceIndex}`);
        if (diagnosisStr) {
            try {
                this.diagnosaForm = JSON.parse(diagnosisStr);
            } catch {
                // Ignore parsing errors
            }
        }

        // Restore resep list
        const resepStr = localStorage.getItem(`resep_${this.caseId}_${this.currentOsceIndex}`);
        if (resepStr) {
            try {
                this.resepList = JSON.parse(resepStr);
                if (this.resepList.length === 0) {
                    this.resepList = [''];
                }
            } catch {
                // Ignore parsing errors
            }
        }

        // Restore interpretasi form
        const interpretasiStr = localStorage.getItem(`interpretasi_${this.caseId}_${this.currentOsceIndex}`);
        if (interpretasiStr) {
            try {
                this.interpretasiForm = JSON.parse(interpretasiStr);
            } catch {
                // Ignore parsing errors
            }
        }

        // Restore RME visibility
        const rmeShowingStr = localStorage.getItem(`rme_showing_${this.caseId}_${this.currentOsceIndex}`);
        if (rmeShowingStr) {
            this.rmeShowing = rmeShowingStr === 'true';
        }

        // Restore activeTab
        const activeTabStr = localStorage.getItem(`activeTab_${this.caseId}_${this.currentOsceIndex}`);
        if (activeTabStr) {
            this.activeTab = parseInt(activeTabStr, 10) || 0;
        }
    }

    // Fetch sheet details for restore without changing active tab
    fetchSheetDetailsListForRestore(selectedCheckbox: string[]) {
        const startTime = Date.now();
        let completed = 0;
        this.sheetDetailsList = [];
        const currentOsceId = this.getCurrentOsce()?.ID_OSCE;
        
        const total = selectedCheckbox.length;
        const tempList: any[] = [];
        
        if (total === 0) {
            return;
        }
        
        selectedCheckbox.forEach((id, i) => {
            this.userService.getSheetDetails({ id }).then(res => {
                if (Array.isArray(res.data)) {
                    // Add all data, prioritizing OSCE-relevant data
                    const relevantData = res.data.filter((d: any) => d.osce_id === currentOsceId);
                    const otherData = res.data.filter((d: any) => d.osce_id !== currentOsceId);
                    
                    // Get the selected examination name for this ID
                    const selectedName = this.checkedSheetDetailsNames[id];
                    
                    // Filter data based on selected examination name
                    let dataToAdd = relevantData.length > 0 ? relevantData : otherData;
                    if (selectedName) {
                        dataToAdd = dataToAdd.filter((d: any) => d.name === selectedName);
                    }
                    
                    tempList.push(...dataToAdd);
                }
                if (!res.data || !res.data.length) {
                }
            }).catch((err) => {
            }).finally(() => {
                completed++;
                
                if (completed === total) {
                    this.sheetDetailsList = tempList;
                    this.groupedSheetDetailsTableResult = this.buildGroupedSheetDetailsTable();
                    
                    // Save sheetDetailsList to localStorage
                    localStorage.setItem(`sheetDetailsList_${this.caseId}_${this.currentOsceIndex}`, JSON.stringify(this.sheetDetailsList));
                    
                    // Don't change activeTab here - stay on pemeriksaan tab
                    
                    // Save interpretasi form to localStorage
                    localStorage.setItem(`interpretasi_${this.caseId}_${this.currentOsceIndex}`, JSON.stringify(this.interpretasiForm));
                }
            });
        });
    }

    initApi(): void {
        this.isLoading = true;
        this.userService.getPackageSimulation().then(res => {
            const current = localStorage.getItem('simulation_currentParticipant');
            this.currentParticipant = current ? JSON.parse(current) : null;
            this.packages = res.data || [];
            this.packages = this.packages.filter(pkg => {
                if (this.currentParticipant) {
                    return pkg.id === this.currentParticipant.package_id;
                }
                return false;
            });

            // Ambil osceList dari packages[0].osce_id
            if (this.packages.length > 0 && this.packages[0].osce_id) {
                try {
                    this.osceList = JSON.parse(this.packages[0].osce_id);
                } catch {
                    this.osceList = [];
                }
            } else {
                this.osceList = [];
            }
            
            // Baca currentOsceIndex dari localStorage, default ke 0
            const savedOsceIndex = localStorage.getItem(this.osceIndexKey);
            this.currentOsceIndex = savedOsceIndex ? parseInt(savedOsceIndex, 10) : 0;
            
            // Pastikan index tidak melebihi jumlah OSCE yang tersedia
            if (this.currentOsceIndex >= this.osceList.length) {
                this.currentOsceIndex = 0;
                localStorage.setItem(this.osceIndexKey, '0');
            }

        }).catch(() => {
            this.packages = [];
        });
        this.userService.getSheetCases({
            id: this.caseId,
            is_actived: 1
        }).then((res) => {
            this.cases = res.data || [];
            this.applySearch();
            this.updateTimerConfig();

            // Timer persistence logic
            // Ambil duration dari this.packages[0].duration (dalam detik, convert ke menit), fallback ke this.cases[0]?.timer
            let timerMinutes = 0;
            if (this.packages && this.packages.length > 0 && this.packages[0].duration) {
                timerMinutes = Math.ceil(Number(this.packages[0].duration) / 60); // convert detik ke menit, dibulatkan ke atas
            } else if (this.cases[0]?.timer) {
                timerMinutes = Number(this.cases[0].timer);
            }
            if (timerMinutes > 0) {
                // Jika simulasi dijeda, jangan buat timer baru
                if (this.isSimulationPaused) {
                } else {
                    // Cek localStorage untuk endTime
                    const savedEndTime = localStorage.getItem(this.countdownKey);
                    const now = Math.floor(Date.now() / 1000);
                    if (savedEndTime && Number(savedEndTime) > now) {
                        this.countdownEndTime = Number(savedEndTime);
                    } else {
                        // Set endTime baru hanya jika tidak ada waktu yang disimpan atau sudah habis
                        this.countdownEndTime = now + timerMinutes * 60;
                        localStorage.setItem(this.countdownKey, String(this.countdownEndTime));
                    }
                }
            }

            this.userService.getSheetDetails(null).then(res => {
                const data = res.data || [];
                this.groupedSheetDetails = {};
                // Tampilkan hanya satu detail untuk setiap nama yang sama (sheet_name + name)
                const seenNames: { [key: string]: Set<string> } = {};
                data.forEach((detail: any) => {
                    if (!this.groupedSheetDetails[detail.sheet_name]) {
                        this.groupedSheetDetails[detail.sheet_name] = [];
                        seenNames[detail.sheet_name] = new Set();
                    }
                    // Jika nama sudah pernah ditambahkan, skip
                    if (!seenNames[detail.sheet_name].has(detail.name)) {
                        this.groupedSheetDetails[detail.sheet_name].push(detail);
                        seenNames[detail.sheet_name].add(detail.name);
                    }
                });
                this.checkedSheetDetails = {};
                if (res.data.pivot && Array.isArray(res.data.pivot)) {
                    res.data.pivot.forEach((p: any) => {
                        if (p.sheet_detail_id && p.interpretasi == "1") {
                            this.checkedSheetDetails[p.sheet_detail_id] = true;
                        }
                    });
                }
                this.searchSheetDetail = '';
                this.applySearchSheetDetail();
                this.isLoading = false;

                // Debug logging

                // Jika ada checkedSheetDetails di localStorage dan ada yang tercentang, fetch konten otomatis
                const checkedIds = Object.keys(this.checkedSheetDetails).filter(id => this.checkedSheetDetails[id]);
                if (checkedIds.length > 0) {
                    this.fetchSheetDetailsList(checkedIds);
                }
            }).catch(() => {
                this.isLoading = false;
            });
        }).catch(() => {
            this.cases = [];
            this.applySearch();
            this.isLoading = false;
        });
    }

    // Method untuk mendapatkan OSCE yang sedang aktif
    getCurrentOsce(): any {
        if (this.osceList && this.osceList.length > 0 && this.currentOsceIndex < this.osceList.length) {
            return this.osceList[this.currentOsceIndex];
        }
        return null;
    }

    // Method untuk mendapatkan kasus yang sesuai dengan OSCE aktif
    getCurrentOsceCase(): any {
        const currentOsce = this.getCurrentOsce();
        if (!currentOsce) {
            // Jika tidak ada OSCE aktif, gunakan kasus pertama
            return this.filteredCases.length > 0 ? this.filteredCases[0] : null;
        }

        // Cari kasus berdasarkan nama OSCE atau mapping yang sesuai
        // Untuk sekarang, kita akan mencoba mencocokkan berdasarkan urutan OSCE
        // atau mencari kasus yang memiliki nama serupa dengan OSCE
        const osceName = currentOsce.NAMA_OSCE;
        

        
        // Prioritas 1: Cari berdasarkan nama yang mirip
        if (osceName) {
            const matchingCase = this.filteredCases.find(caseItem => {
                if (!caseItem.name) return false;
                const caseName = caseItem.name.toLowerCase();
                const osceNameLower = osceName.toLowerCase();
                
                // Cek apakah ada kesamaan kata kunci
                const osceKeywords = osceNameLower.split(' ');
                return osceKeywords.some((keyword: string) => 
                    keyword.length > 3 && caseName.includes(keyword)
                );
            });
            
            if (matchingCase) {
                return matchingCase;
            }
        }

        // Prioritas 2: Gunakan index OSCE untuk mapping langsung
        const caseIndex = this.currentOsceIndex;
        if (caseIndex < this.filteredCases.length) {
            return this.filteredCases[caseIndex];
        }

        // Fallback: gunakan kasus pertama
        return this.filteredCases.length > 0 ? this.filteredCases[0] : null;
    }

    applySearchSheetDetail() {
        const keyword = (this.searchSheetDetail || '').toLowerCase();
        this.filteredGroupedSheetDetails = {};
        for (const group of this.groupedSheetDetailKeys()) {
            if (!keyword) {
                this.filteredGroupedSheetDetails[group] = this.groupedSheetDetails[group] || [];
            } else {
                this.filteredGroupedSheetDetails[group] = (this.groupedSheetDetails[group] || []).filter((detail: any) => {
                    return detail.name && detail.name.toLowerCase().includes(keyword);
                });
            }
        }
    }

    groupedSheetDetailKeys() {        
        return this.groupedSheetDetails ? Object.keys(this.groupedSheetDetails) : [];
    }

    applySearch(): void {
        const keyword = (this.searchText || '').toLowerCase();
        this.filteredCases = this.cases.filter(item =>
            !keyword ||
            (item.name && item.name.toLowerCase().includes(keyword)) ||
            (item.description && item.description.toLowerCase().includes(keyword))
        );
        
    }

    // Tambahkan fungsi ini ke dalam class
    isFilteredSheetDetailEmpty(): boolean {
        if (!this.filteredGroupedSheetDetails) return true;
        for (const group of this.groupedSheetDetailKeys()) {
            if (this.filteredGroupedSheetDetails[group]?.length > 0) {
                return false;
            }
        }
        return true;
    }

    // Method untuk mengecek apakah tidak ada data karena filter OSCE
    isNoDataForCurrentOsce(): boolean {
        const currentOsce = this.getCurrentOsce();
        const currentOsceId = currentOsce?.ID_OSCE;
        
        // Jika tidak ada OSCE ID, return false
        if (!currentOsceId) return false;
        
        // Jika tidak ada pencarian dan tidak ada data di groupedSheetDetails, 
        // kemungkinan karena filter OSCE
        const hasSearchKeyword = !!(this.searchSheetDetail && this.searchSheetDetail.trim() !== '');
        const hasGroupedData = !!(this.groupedSheetDetails && Object.keys(this.groupedSheetDetails).length > 0);
        
        // Jika tidak ada pencarian dan tidak ada data yang terfilter, berarti tidak ada data untuk OSCE ini
        return !hasSearchKeyword && !hasGroupedData;
    }

    // Method untuk mengecek apakah kosong karena pencarian
    isEmptyDueToSearch(): boolean {
        const hasSearchKeyword = !!(this.searchSheetDetail && this.searchSheetDetail.trim() !== '');
        const hasDataWithoutSearch = !!(this.groupedSheetDetails && Object.keys(this.groupedSheetDetails).length > 0);
        
        return hasSearchKeyword && hasDataWithoutSearch && this.isFilteredSheetDetailEmpty();
    }

    // Save form data methods
    saveDiagnosisData(): void {
        localStorage.setItem(`diagnosis_${this.caseId}_${this.currentOsceIndex}`, JSON.stringify(this.diagnosaForm));
    }

    saveResepData(): void {
        localStorage.setItem(`resep_${this.caseId}_${this.currentOsceIndex}`, JSON.stringify(this.resepList));
    }

    saveInterpretasiData(): void {
        localStorage.setItem(`interpretasi_${this.caseId}_${this.currentOsceIndex}`, JSON.stringify(this.interpretasiForm));
    }

    isInterpretasiComplete(): boolean {
        // Periksa apakah semua textarea interpretasi yang diperlukan sudah diisi
        for (const group of this.sheetDetailsList) {
            if (group.interpretasi == 1) {
                const key = group.sheet_name + '||' + group.header;
                const value = this.interpretasiForm[key];
                if (!value || value.trim() === '') {
                    return false;
                }
            }
        }
        return true;
    }

    toggleCheckbox(detail: any) {
        if (this.isSimulationPaused) return;
        if (this.isMaxChecked && !this.checkedSheetDetails[detail.id]) return;
        this.checkedSheetDetails[detail.id] = !this.checkedSheetDetails[detail.id];
        // Simpan ke localStorage setiap kali berubah
        localStorage.setItem('checkedSheetDetails', JSON.stringify(this.checkedSheetDetails));
        this.onInterpretasiChange(detail, this.checkedSheetDetails[detail.id]);
        this.updateMaxChecked();
    }

    onInterpretasiChange(detail: any, checked: boolean) {
        // Simpan atau hapus nama pemeriksaan yang dipilih
        if (checked) {
            this.checkedSheetDetailsNames[detail.id] = detail.name;
        } else {
            delete this.checkedSheetDetailsNames[detail.id];
        }
        
        // Simpan ke localStorage
        localStorage.setItem('checkedSheetDetailsNames', JSON.stringify(this.checkedSheetDetailsNames));
        
        this.updateMaxChecked();
        // ...tambahkan logic lain jika perlu...
    }

    updateMaxChecked() {
        const checkedCount = Object.values(this.checkedSheetDetails).filter(v => v).length;
        this.isMaxChecked = checkedCount >= 5; // Maksimal 5, tapi minimal 1 untuk lanjut
    }

    getTimerSeconds(timer: any): number {
        // Jika simulasi dijeda, return waktu yang tersimpan (untuk display static)
        // Tapi karena sekarang kita pakai *ngIf, method ini tidak akan dipanggil saat pause
        
        // timer bisa string atau number (menit)
        if (this.countdownEndTime) {
            const now = Math.floor(Date.now() / 1000);
            const left = this.countdownEndTime - now;
            return left > 0 ? left : 0;
        }
        const t = Number(timer);
        if (isNaN(t) || t <= 0) return 0;
        return t * 60;
    }

    formatPausedTime(): string {
        const pausedTimeLeft = localStorage.getItem(`paused_time_left_${this.caseId}`);
        if (pausedTimeLeft) {
            const seconds = parseInt(pausedTimeLeft, 10);
            const minutes = Math.floor(seconds / 60);
            const remainingSeconds = seconds % 60;
            return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
        }
        return '00:00';
    }

    onCountdownEvent(event: CountdownEvent) {
        // Jangan proses event jika simulasi dijeda
        if (this.isSimulationPaused) {
            return;
        }
        
        if (event.action === 'done') {
            localStorage.removeItem(this.countdownKey);
            
            // Jika masih ada OSCE berikutnya, auto-submit dan pindah
            if (this.osceList && this.currentOsceIndex < this.osceList.length - 1) {
                
                // Auto-submit dengan data yang ada saat ini
                const pemeriksaan: any = {};
                for (const key in this.interpretasiForm) {
                    if (this.interpretasiForm[key] && this.interpretasiForm[key].trim() !== '') {
                        pemeriksaan[key] = this.interpretasiForm[key];
                    }
                }
                
                // Tambahkan key pemeriksaan default jika interpretasi tidak ada
                const allSheetNames = Object.keys(this.groupedSheetDetails || {});
                for (const sheetName of allSheetNames) {
                    const details = this.groupedSheetDetails[sheetName] || [];
                    const uniqueHeaders = Array.from(new Set(details.map((d: any) => d.header)));
                    if (uniqueHeaders.length === 0) {
                        const key = `${sheetName}||null`;
                        if (!Object.keys(pemeriksaan).some(k => k.startsWith(sheetName))) {
                            pemeriksaan[key] = '';
                        }
                    } else {
                        for (const header of uniqueHeaders) {
                            const key = `${sheetName}||${header}`;
                            if (!Object.keys(pemeriksaan).some(k => k === key)) {
                                pemeriksaan[key] = '';
                            }
                        }
                    }
                }
                
                const diagnosa = {
                    diagnosaUtama: this.diagnosaForm.diagnosaUtama,
                    diagnosaBanding1: this.diagnosaForm.diagnosaBanding1,
                    diagnosaBanding2: this.diagnosaForm.diagnosaBanding2
                };
                
                const resep = this.resepList.filter(r => r && r.trim() !== '');
                const userStr = localStorage.getItem('user');
                const user = userStr ? JSON.parse(userStr) : {};
                const user_id = user.ID_USER || user.id || user.ID || '';
                const current = localStorage.getItem('simulation_currentParticipant');
                this.currentParticipant = current ? JSON.parse(current) : null;
                const osce_id = this.osceList[this.currentOsceIndex]?.ID_OSCE;

                const payload = {
                    pemeriksaan: JSON.stringify(pemeriksaan),
                    diagnosa: JSON.stringify(diagnosa),
                    resep: JSON.stringify(resep),
                    user_id: user_id,
                    sheet_case_id: this.cases[0]?.id || null,
                    sheet_detail_id: JSON.stringify(Object.keys(this.checkedSheetDetails).filter(id => this.checkedSheetDetails[id])),
                    simulation_id: this.currentParticipant?.id || null,
                    osce_id: osce_id || null
                };

                this.userService.addSheetAnswer(payload).then(() => {
                    const currentOsceNumber = this.currentOsceIndex + 1;
                    const nextOsceNumber = this.currentOsceIndex + 2;
                    
                    this.messageService.add({ 
                        severity: 'info', 
                        summary: 'Waktu Habis', 
                        detail: `Waktu OSCE ${currentOsceNumber} habis. Berlanjut ke OSCE ${nextOsceNumber}.` 
                    });
                    
                    // Pindah ke OSCE berikutnya
                    this.currentOsceIndex++;
                    localStorage.setItem(this.osceIndexKey, String(this.currentOsceIndex));
                    
                    // Reset state untuk OSCE baru
                    this.resetForNextOsce();
                    
                    // Reset timer untuk OSCE berikutnya
                    this.resetTimerForNextOsce();
                    
                    // Load kasus untuk OSCE berikutnya
                    this.loadCaseForCurrentOsce();
                }).catch(() => {
                    this.messageService.add({ 
                        severity: 'error', 
                        summary: 'Gagal', 
                        detail: 'Gagal menyimpan jawaban OSCE saat waktu habis.' 
                    });
                });
                
            } else {
                // OSCE terakhir, submit final
                if (!this.isSubmitted && !this.isLoading) {
                    this.onSubmit();
                    this.messageService.add({ 
                        severity: 'warn', 
                        summary: 'Waktu Habis', 
                        detail: 'Waktu pengerjaan telah habis. Jawaban otomatis dikumpulkan.' 
                    });
                }
            }
        }
    }

    // Fungsi untuk submit jawaban OSCE saat ini (tanpa set isSubmitted)
    submitCurrentOsceAnswer() {
        // ...ambil data pemeriksaan, diagnosa, resep seperti di onSubmit...
        const pemeriksaan: any = {};
        for (const key in this.interpretasiForm) {
            if (this.interpretasiForm[key] && this.interpretasiForm[key].trim() !== '') {
                pemeriksaan[key] = this.interpretasiForm[key];
            }
        }
        if (!Object.keys(pemeriksaan).some(k => k.startsWith('RADIOLOGI'))) {
            pemeriksaan['RADIOLOGI||null'] = '';
        }
        if (!Object.keys(pemeriksaan).some(k => k.startsWith('LABORATORIUM'))) {
            pemeriksaan['LABORATORIUM||["Parameter ","Hasil","Satuan","Rujukan Dewasa","Rujukan Anak"]'] = '';
        }
        const diagnosa = {
            diagnosaUtama: this.diagnosaForm.diagnosaUtama,
            diagnosaBanding1: this.diagnosaForm.diagnosaBanding1,
            diagnosaBanding2: this.diagnosaForm.diagnosaBanding2
        };
        const resep = this.resepList.filter(r => r && r.trim() !== '');
        const userStr = localStorage.getItem('user');
        const user = userStr ? JSON.parse(userStr) : {};
        const user_id = user.ID_USER || user.id || user.ID || '';
        const current = localStorage.getItem('simulation_currentParticipant');
        this.currentParticipant = current ? JSON.parse(current) : null;
        const osce_id = this.osceList[this.currentOsceIndex]?.ID_OSCE;



        const payload = {
            pemeriksaan: JSON.stringify(pemeriksaan),
            diagnosa: JSON.stringify(diagnosa),
            resep: JSON.stringify(resep),
            user_id: user_id,
            sheet_case_id: this.cases[0]?.id || null,
            sheet_detail_id: JSON.stringify(Object.keys(this.checkedSheetDetails).filter(id => this.checkedSheetDetails[id])),
            simulation_id: this.currentParticipant?.id || null,
            osce_id: osce_id || null
        };

        this.userService.addSheetAnswer(payload).then(() => {
            // Jangan increment currentOsceIndex di sini!
            this.resetForNextOsce();
            this.messageService.add({ severity: 'success', summary: 'Berhasil', detail: 'Jawaban OSCE berhasil dikumpulkan.' });
            // Pindahkan ke OSCE berikutnya
            this.currentOsceIndex++;
            localStorage.setItem(this.osceIndexKey, String(this.currentOsceIndex));
            // Load kasus untuk OSCE berikutnya tanpa restart timer
            this.loadCaseForCurrentOsce();
        }).catch(() => {
            this.messageService.add({ severity: 'error', summary: 'Gagal', detail: 'Gagal mengumpulkan jawaban OSCE.' });
        });
    }

    // Fungsi untuk submit jawaban OSCE saat ini via tombol (tanpa restart timer)
    submitCurrentOsceAnswerViaButton() {
        this.isLoading = true;
        // ...ambil data pemeriksaan, diagnosa, resep seperti di onSubmit...
        const pemeriksaan: any = {};
        for (const key in this.interpretasiForm) {
            if (this.interpretasiForm[key] && this.interpretasiForm[key].trim() !== '') {
                pemeriksaan[key] = this.interpretasiForm[key];
            }
        }
        
        // Tambahkan key pemeriksaan default jika interpretasi tidak ada
        const allSheetNames = Object.keys(this.groupedSheetDetails || {});
        for (const sheetName of allSheetNames) {
            const details = this.groupedSheetDetails[sheetName] || [];
            const uniqueHeaders = Array.from(new Set(details.map((d: any) => d.header)));
            if (uniqueHeaders.length === 0) {
                const key = `${sheetName}||null`;
                if (!Object.keys(pemeriksaan).some(k => k.startsWith(sheetName))) {
                    pemeriksaan[key] = '';
                }
            } else {
                for (const header of uniqueHeaders) {
                    const key = `${sheetName}||${header}`;
                    if (!Object.keys(pemeriksaan).some(k => k === key)) {
                        pemeriksaan[key] = '';
                    }
                }
            }
        }
        
        const diagnosa = {
            diagnosaUtama: this.diagnosaForm.diagnosaUtama,
            diagnosaBanding1: this.diagnosaForm.diagnosaBanding1,
            diagnosaBanding2: this.diagnosaForm.diagnosaBanding2
        };
        const resep = this.resepList.filter(r => r && r.trim() !== '');
        const userStr = localStorage.getItem('user');
        const user = userStr ? JSON.parse(userStr) : {};
        const user_id = user.ID_USER || user.id || user.ID || '';
        const current = localStorage.getItem('simulation_currentParticipant');
        this.currentParticipant = current ? JSON.parse(current) : null;
        const osce_id = this.osceList[this.currentOsceIndex]?.ID_OSCE;

        const payload = {
            pemeriksaan: JSON.stringify(pemeriksaan),
            diagnosa: JSON.stringify(diagnosa),
            resep: JSON.stringify(resep),
            user_id: user_id,
            sheet_case_id: this.cases[0]?.id || null,
            sheet_detail_id: JSON.stringify(Object.keys(this.checkedSheetDetails).filter(id => this.checkedSheetDetails[id])),
            simulation_id: this.currentParticipant?.id || null,
            osce_id: osce_id || null
        };

        this.userService.addSheetAnswer(payload).then(() => {
            this.resetForNextOsce();
            this.messageService.add({ severity: 'success', summary: 'Berhasil', detail: 'Jawaban OSCE berhasil dikumpulkan.' });
            // Pindahkan ke OSCE berikutnya
            this.currentOsceIndex++;
            localStorage.setItem(this.osceIndexKey, String(this.currentOsceIndex));
            // Load kasus untuk OSCE berikutnya tanpa restart timer
            this.loadCaseForCurrentOsce();
            this.isLoading = false;
        }).catch(() => {
            this.isLoading = false;
            this.messageService.add({ severity: 'error', summary: 'Gagal', detail: 'Gagal mengumpulkan jawaban OSCE.' });
        });
    }

    // Reset state untuk OSCE berikutnya (kecuali timer)
    resetForNextOsce() {
        this.isSubmitted = false;
        this.activeTab = 0;
        this.sheetDetailsList = [];
        this.groupedSheetDetailsTableResult = [];
        this.checkedSheetDetails = {};
        this.checkedSheetDetailsNames = {};
        this.filteredGroupedSheetDetails = {};
        this.isMaxChecked = false;
        this.searchSheetDetail = '';
        this.interpretasiForm = {};
        this.diagnosaForm = {
            diagnosaUtama: '',
            diagnosaBanding1: '',
            diagnosaBanding2: ''
        };
        this.resepList = [''];
        
        // Clear localStorage for current OSCE
        localStorage.removeItem('checkedSheetDetails');
        localStorage.removeItem('checkedSheetDetailsNames');
        localStorage.removeItem(`diagnosis_${this.caseId}_${this.currentOsceIndex}`);
        localStorage.removeItem(`resep_${this.caseId}_${this.currentOsceIndex}`);
        localStorage.removeItem(`interpretasi_${this.caseId}_${this.currentOsceIndex}`);
        localStorage.removeItem(`rme_showing_${this.caseId}_${this.currentOsceIndex}`);
        localStorage.removeItem(`paused_time_left_${this.caseId}`);
        

    }

    // Load kasus untuk OSCE aktif dengan reset timer
    loadCaseForCurrentOsce() {
        const osceId = this.osceList[this.currentOsceIndex]?.ID_OSCE;
        if (!osceId) return;
        this.isLoading = true;
                
        // Load kasus untuk OSCE saat ini
        this.userService.getSheetCases({
            id: this.caseId,
            is_actived: 1
        }).then((res) => {
            this.cases = res.data || [];
            this.applySearch();
            this.updateTimerConfig();
            
            // Load sheet details yang sesuai dengan OSCE saat ini
            this.userService.getSheetDetails(null).then(sheetRes => {
                const data = sheetRes.data || [];
                this.groupedSheetDetails = {};
                
                // PERBAIKAN: Jangan filter berdasarkan OSCE ID, ambil semua data
                // Karena pemeriksaan bisa digunakan di berbagai OSCE

                
                // Tampilkan hanya satu detail untuk setiap nama yang sama (sheet_name + name)
                const seenNames: { [key: string]: Set<string> } = {};
                data.forEach((detail: any) => {
                    if (!this.groupedSheetDetails[detail.sheet_name]) {
                        this.groupedSheetDetails[detail.sheet_name] = [];
                        seenNames[detail.sheet_name] = new Set();
                    }
                    // Jika nama sudah pernah ditambahkan, skip
                    if (!seenNames[detail.sheet_name].has(detail.name)) {
                        this.groupedSheetDetails[detail.sheet_name].push(detail);
                        seenNames[detail.sheet_name].add(detail.name);
                    }
                });
                
                this.searchSheetDetail = '';
                this.applySearchSheetDetail();
                this.isLoading = false;
                

                
                // Log detail jumlah pemeriksaan per group
                for (const groupName of Object.keys(this.groupedSheetDetails)) {

                }
            }).catch(() => {
                this.isLoading = false;

            });
        }).catch(() => {
            this.cases = [];
            this.applySearch();
            this.isLoading = false;

        });
    }

    // Pastikan parseHeader adalah property arrow function agar bisa dipanggil dari template
    parseHeader = (header: string): string[] => {
        if (!header) return [];
        try {
            const parsed = JSON.parse(header);
            return Array.isArray(parsed) ? parsed : [parsed];
        } catch {
            return [header];
        }
    }

    // Pastikan getTableRows adalah property arrow function agar bisa dipanggil dari template
    getTableRows = (group: any): string[][] => {
        let allRows: string[][] = [];
        const headers = this.parseHeader(group.header);
        for (const detail of group.details) {
            try {
                const arr = JSON.parse(detail.content);
                // Pastikan array panjangnya sama dengan headers, jika kurang, tambahkan ""
                const row: string[] = [];
                for (let i = 0; i < headers.length; i++) {
                    let val = (arr[i] !== undefined && arr[i] !== null && arr[i].toString().trim() !== '') ? arr[i] : '-';
                    row.push(val);
                }
                // Jika seluruh row kosong (semua "-"), skip
                if (row.some(cell => cell !== '-')) {
                    allRows.push(row);
                }
            } catch {
                // skip error
            }
        }
        return allRows;
    }

    onSubmit() {
        // Kumpulkan data pemeriksaan (interpretasi)
        const pemeriksaan: any = {};
        // Simpan interpretasi yang diisi user
        for (const key in this.interpretasiForm) {
            if (this.interpretasiForm[key] && this.interpretasiForm[key].trim() !== '') {
                pemeriksaan[key] = this.interpretasiForm[key];
            }
        }

        // Tambahkan key pemeriksaan default jika interpretasi tidak ada
        // Buat dinamis berdasarkan semua sheet_name yang ada di groupedSheetDetails
        const allSheetNames = Object.keys(this.groupedSheetDetails || {});
        for (const sheetName of allSheetNames) {
            const details = this.groupedSheetDetails[sheetName] || [];
            const uniqueHeaders = Array.from(new Set(details.map((d: any) => d.header)));
            if (uniqueHeaders.length === 0) {
                const key = `${sheetName}||null`;
                if (!Object.keys(pemeriksaan).some(k => k.startsWith(sheetName))) {
                    pemeriksaan[key] = '';
                }
            } else {
                for (const header of uniqueHeaders) {
                    const key = `${sheetName}||${header}`;
                    if (!Object.keys(pemeriksaan).some(k => k === key)) {
                        pemeriksaan[key] = '';
                    }
                }
            }
        }

        // Diagnosa
        const diagnosa = {
            diagnosaUtama: this.diagnosaForm.diagnosaUtama,
            diagnosaBanding1: this.diagnosaForm.diagnosaBanding1,
            diagnosaBanding2: this.diagnosaForm.diagnosaBanding2
        };

        // Resep
        const resep = this.resepList.filter(r => r && r.trim() !== '');

        // User ID
        const userStr = localStorage.getItem('user');
        const user = userStr ? JSON.parse(userStr) : {};
        const user_id = user.ID_USER || user.id || user.ID || '';
        const current = localStorage.getItem('simulation_currentParticipant');
        this.currentParticipant = current ? JSON.parse(current) : null;
        const osce_id = this.osceList && this.osceList.length > 0
            ? this.osceList[this.currentOsceIndex]?.ID_OSCE
            : null;

        const payload = {
            pemeriksaan: JSON.stringify(pemeriksaan),
            diagnosa: JSON.stringify(diagnosa),
            resep: JSON.stringify(resep),
            user_id: user_id,
            sheet_case_id: this.cases[0]?.id || null,
            sheet_detail_id: JSON.stringify(Object.keys(this.checkedSheetDetails).filter(id => this.checkedSheetDetails[id])),
            simulation_id: this.currentParticipant?.id || null,
            osce_id: osce_id || null
        };

        this.isLoading = true;

        // Jika masih ada OSCE berikutnya, submit dan pindah ke OSCE berikutnya
        if (this.osceList && this.currentOsceIndex < this.osceList.length - 1) {
            this.userService.addSheetAnswer(payload).then(() => {
                const currentOsceNumber = this.currentOsceIndex + 1;
                const nextOsceNumber = this.currentOsceIndex + 2;
                
                this.messageService.add({ 
                    severity: 'success', 
                    summary: 'Berhasil', 
                    detail: `OSCE ${currentOsceNumber} selesai. Lanjut ke OSCE ${nextOsceNumber}.` 
                });
                
                // Pindah ke OSCE berikutnya
                this.currentOsceIndex++;
                localStorage.setItem(this.osceIndexKey, String(this.currentOsceIndex));
                                
                // Reset state untuk OSCE baru
                this.resetForNextOsce();
                
                // Reset timer untuk OSCE berikutnya
                this.resetTimerForNextOsce();
                
                // Load kasus untuk OSCE berikutnya
                this.loadCaseForCurrentOsce();
                
                this.isLoading = false;
            }).catch(() => {
                this.isLoading = false;
                this.messageService.add({ 
                    severity: 'error', 
                    summary: 'Gagal', 
                    detail: 'Gagal menyimpan jawaban OSCE. Silakan coba lagi.' 
                });
            });
        } else {
            // OSCE terakhir, set isSubmitted
            this.userService.addSheetAnswer(payload).then(() => {
                this.isLoading = false;
                this.isSubmitted = true;
                localStorage.setItem(this.submittedLockKey, '1');
                // Simpan current index untuk OSCE terakhir
                localStorage.setItem(this.osceIndexKey, String(this.currentOsceIndex));
                this.messageService.add({ 
                    severity: 'success', 
                    summary: 'Selamat!', 
                    detail: 'Semua OSCE telah selesai dikerjakan.' 
                });
                // Clear local storage sebelum navigasi
                this.clearAllSimulationData();
                // Navigasi ke history setelah submit berhasil
                this.router.navigate(['/app/osce/sheet/history']);
            }).catch(() => {
                this.isLoading = false;
                this.messageService.add({ severity: 'error', summary: 'Gagal', detail: 'Gagal mengumpulkan jawaban. Silakan coba lagi.' });
            });
        }
    }

    goHome() {
        // Hapus localStorage yang spesifik untuk OSCE ini
        localStorage.removeItem('checkedSheetDetails');
        localStorage.removeItem('checkedSheetDetailsNames');
        localStorage.removeItem('simulation_currentParticipant');
        localStorage.removeItem(this.countdownKey);
        localStorage.removeItem(this.submittedLockKey);
        localStorage.removeItem(this.osceIndexKey);
        localStorage.removeItem('current_case_id');
        
        // Kumpulkan semua keys yang perlu dihapus terlebih dahulu
        const keysToRemove: string[] = [];
        for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i);
            if (key && (key.startsWith('current_osce_index_') || key.startsWith('countdown_endTime_'))) {
                keysToRemove.push(key);
            }
        }
        
        // Hapus keys yang sudah dikumpulkan
        keysToRemove.forEach(key => localStorage.removeItem(key));
        
        // Navigasi menggunakan Angular Router
        this.router.navigate(['/app/dashboard']);
    }

    exitSimulation() {
        // Tampilkan konfirmasi sebelum keluar
        if (!confirm('Apakah Anda yakin ingin keluar dari simulasi? Semua progress akan hilang dan simulasi akan direset.')) {
            return;
        }
        
        // Bersihkan semua localStorage yang berhubungan dengan simulasi ini
        this.clearAllSimulationData();
        
        // Reset partisipan simulasi (agar tutor bisa memberikan penilaian)
        this.resetSimulationParticipant();
        
        // Tampilkan pesan konfirmasi
        this.messageService.add({ 
            severity: 'info', 
            summary: 'Simulasi Dihentikan', 
            detail: 'Anda telah keluar dari simulasi. Semua data simulasi telah dibersihkan.' 
        });
        
        // Tunggu sebentar untuk menampilkan pesan, lalu redirect
        setTimeout(() => {
            this.router.navigate(['/app/dashboard']);
        }, 2000);
    }

    private clearAllSimulationData() {
        
        // Hapus data simulasi spesifik untuk case ini
        localStorage.removeItem('checkedSheetDetails');
        localStorage.removeItem('checkedSheetDetailsNames');
        localStorage.removeItem('simulation_currentParticipant');
        localStorage.removeItem(this.countdownKey);
        localStorage.removeItem(this.submittedLockKey);
        localStorage.removeItem(this.osceIndexKey);
        localStorage.removeItem('current_case_id');
        
        // Hapus data form untuk semua OSCE index yang mungkin ada
        const currentCaseId = this.caseId;
        const keysToRemove: string[] = [];
        
        for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i);
            if (key) {
                // Hapus semua key yang berhubungan dengan simulasi ini
                if (key.startsWith('current_osce_index_') ||
                    key.startsWith('countdown_endTime_') ||
                    key.startsWith('osce_is_submitted_') ||
                    key.startsWith('paused_time_left_') ||
                    key.startsWith(`diagnosis_${currentCaseId}_`) ||
                    key.startsWith(`resep_${currentCaseId}_`) ||
                    key.startsWith(`interpretasi_${currentCaseId}_`)) {
                    keysToRemove.push(key);
                }
            }
        }
        
        // Hapus semua keys yang sudah dikumpulkan
        keysToRemove.forEach(key => {
            localStorage.removeItem(key);
        });
        
    }

    private resetSimulationParticipant() {
        // Reset status partisipan simulasi agar tutor bisa memberikan penilaian
        if (this.currentParticipant && this.currentParticipant.id) {
            
            // Panggil API untuk reset status partisipan (jika ada endpoint)
            // this.userService.resetSimulationParticipant(this.currentParticipant.id)
            //     .then(() => {
            //         console.log('[SheetAnswer] resetSimulationParticipant: Berhasil reset partisipan');
            //     })
            //     .catch(err => {
            //         console.error('[SheetAnswer] resetSimulationParticipant: Error reset partisipan:', err);
            //     });
        }
    }

    pauseSimulation() {
        if (!confirm('Apakah Anda yakin ingin menjeda simulasi? Timer akan dihentikan sementara.')) {
            return;
        }

        
        // Simpan waktu sisa ke localStorage
        if (this.countdownEndTime) {
            const now = Math.floor(Date.now() / 1000);
            const timeLeft = this.countdownEndTime - now;
            if (timeLeft > 0) {
                localStorage.setItem(`paused_time_left_${this.caseId}`, timeLeft.toString());
            }
        }
        
        // Set status pause - ini akan menghentikan countdown component melalui *ngIf
        this.isSimulationPaused = true;
        
        // Hapus countdown end time agar tidak terus berjalan
        localStorage.removeItem(this.countdownKey);
        this.countdownEndTime = null;
        
        this.messageService.add({ 
            severity: 'info', 
            summary: 'Simulasi Dijeda', 
            detail: 'Simulasi telah dijeda. Anda dapat melanjutkan kapan saja.' 
        });
    }

    resumeSimulation() {
        
        // Restore waktu dari localStorage
        const pausedTimeLeft = localStorage.getItem(`paused_time_left_${this.caseId}`);
        if (pausedTimeLeft) {
            const timeLeft = parseInt(pausedTimeLeft, 10);
            const now = Math.floor(Date.now() / 1000);
            this.countdownEndTime = now + timeLeft;
            localStorage.setItem(this.countdownKey, String(this.countdownEndTime));
            localStorage.removeItem(`paused_time_left_${this.caseId}`);
        }
        
        // Set status resume - ini akan menampilkan kembali countdown component
        this.isSimulationPaused = false;
        
        this.messageService.add({ 
            severity: 'success', 
            summary: 'Simulasi Dilanjutkan', 
            detail: 'Simulasi telah dilanjutkan. Timer akan berjalan kembali.' 
        });
    }

    onTabChange(event: any) {
        const newTabIndex = parseInt(event.index?.toString() || event.value?.toString() || '0', 10);
        
        // Prevent navigation to tabs that are not yet unlocked
        if (newTabIndex > this.activeTab + 1) {
            this.messageService.add({
                severity: 'warn',
                summary: 'Tab Belum Tersedia',
                detail: 'Silakan lengkapi tab sebelumnya terlebih dahulu.'
            });
            return;
        }
        
        // Validate that current tab is complete before allowing navigation away
        if (!this.isTabComplete(this.activeTab)) {
            this.messageService.add({
                severity: 'warn',
                summary: 'Tab Belum Lengkap',
                detail: 'Silakan lengkapi semua input yang diperlukan di tab ini sebelum pindah.'
            });
            return;
        }
        
        // Save current tab data before switching
        if (this.activeTab === 1) {
            this.saveDiagnosisData();
        } else if (this.activeTab === 2) {
            this.saveResepData();
        }
        
        this.activeTab = newTabIndex;
        localStorage.setItem(`activeTab_${this.caseId}_${this.currentOsceIndex}`, this.activeTab.toString());
    }

    goToNextTab() {
        // Validate current tab before proceeding
        if (!this.isCurrentTabComplete()) {
            this.messageService.add({
                severity: 'warn',
                summary: 'Input Belum Lengkap',
                detail: 'Silakan lengkapi semua input yang diperlukan sebelum melanjutkan.'
            });
            return;
        }
        
        if (this.activeTab === 0) {
            // From Pemeriksaan to Diagnosis
            const selectedCheckbox = Object.keys(this.checkedSheetDetails).filter(id => this.checkedSheetDetails[id]);
            
            if (!selectedCheckbox.length) {
                this.messageService.add({ 
                    severity: 'warn', 
                    summary: 'Pilih Pemeriksaan', 
                    detail: 'Silakan pilih minimal 1 pemeriksaan untuk melanjutkan.' 
                });
                return;
            }

            // Save checked details to localStorage
            localStorage.setItem('checkedSheetDetails', JSON.stringify(this.checkedSheetDetails));
            
            // Fetch sheet details but stay on current tab for review
            this.fetchSheetDetailsList(selectedCheckbox);
            
        } else if (this.activeTab === 1) {
            // From Diagnosis to Terapi
            // Save diagnosis form data
            localStorage.setItem(`diagnosis_${this.caseId}_${this.currentOsceIndex}`, JSON.stringify(this.diagnosaForm));
            this.activeTab = 2;
            
        } else if (this.activeTab < 2) {
            // General next tab logic
            this.activeTab++;
        }
    }

    fetchSheetDetailsList(selectedCheckbox: string[]) {
        this.isPreparingContent = true;
        const startTime = Date.now();
        let completed = 0;
        this.sheetDetailsList = [];
        const currentOsceId = this.getCurrentOsce()?.ID_OSCE;
        
        const total = selectedCheckbox.length;
        const tempList: any[] = [];
        
        if (total === 0) {
            this.isPreparingContent = false;
            this.messageService.add({ 
                severity: 'warn', 
                summary: 'Pilih Pemeriksaan', 
                detail: 'Silakan pilih minimal 1 pemeriksaan untuk melanjutkan.' 
            });
            return;
        }
        
        selectedCheckbox.forEach((id, i) => {
            this.userService.getSheetDetails({ id }).then(res => {
                if (Array.isArray(res.data)) {
                    // Add all data, prioritizing OSCE-relevant data
                    const relevantData = res.data.filter((d: any) => d.osce_id === currentOsceId);
                    const otherData = res.data.filter((d: any) => d.osce_id !== currentOsceId);
                    
                    // Get the selected examination name for this ID
                    const selectedName = this.checkedSheetDetailsNames[id];
                    
                    // Filter data based on selected examination name
                    let dataToAdd = relevantData.length > 0 ? relevantData : otherData;
                    if (selectedName) {
                        dataToAdd = dataToAdd.filter((d: any) => d.name === selectedName);
                    }
                    
                    tempList.push(...dataToAdd);
                }
                if (!res.data || !res.data.length) {
                }
            }).catch((err) => {
                this.messageService.add({ 
                    severity: 'error', 
                    summary: 'Error', 
                    detail: 'Gagal mengambil detail pemeriksaan untuk beberapa item' 
                });
            }).finally(() => {
                completed++;
                
                if (completed === total) {
                    this.sheetDetailsList = tempList;

                    const data = this.buildGroupedSheetDetailsTable();
                    this.groupedSheetDetailsTableResult = data.filter(item => item.details.is_show !== "0");

                    this.isPreparingContent = false;
                    
                    console.log('Loading selesai. groupedSheetDetailsTableResult:', this.groupedSheetDetailsTableResult);
                    console.log('sheetDetailsList:', this.sheetDetailsList);

                    if (this.groupedSheetDetailsTableResult.length === 0) {
                        this.activeTab = 1;
                    }
                    
                    // Save interpretasi form to localStorage
                    localStorage.setItem(`interpretasi_${this.caseId}_${this.currentOsceIndex}`, JSON.stringify(this.interpretasiForm));
                }
            });
        });
    }

    goToNextTabAfterSheetDetails() {
        // Validate that all required interpretations are filled
        if (!this.isCurrentTabComplete()) {
            this.messageService.add({
                severity: 'warn',
                summary: 'Interpretasi Belum Lengkap',
                detail: 'Silakan lengkapi semua interpretasi pemeriksaan sebelum melanjutkan ke diagnosis.'
            });
            return;
        }
        
        console.log('Tombol "Lanjutkan ke Diagnosis" diklik. Mengubah tab ke Diagnosis (tab 1).');
        console.log('Interpretasi form:', this.interpretasiForm);
        
        // Move to diagnosis tab
        this.activeTab = 1;
        // Don't remove checkedSheetDetails - keep them for potential modifications
    }

    // Method untuk lanjut ke OSCE berikutnya setelah diagnosis
    goToNextTabFromDiagnosis() {
        // OSCE ini belum selesai, lanjut ke tab terapi dulu
        this.activeTab++;
    }

    // Reset timer untuk OSCE berikutnya
    resetTimerForNextOsce() {
        // Hapus timer lama
        localStorage.removeItem(this.countdownKey);
        
        // Update countdown key untuk OSCE berikutnya
        this.countdownKey = `countdown_endTime_${this.caseId}_${this.currentOsceIndex}`;
        
        // Set timer baru untuk OSCE berikutnya
        let timerMinutes = 0;
        if (this.packages && this.packages.length > 0 && this.packages[0].duration) {
            timerMinutes = Math.floor(this.packages[0].duration / 60);
        } else if (this.cases[0]?.timer) {
            timerMinutes = parseInt(this.cases[0].timer, 10) || 0;
        }
        
        if (timerMinutes > 0) {
            const now = Math.floor(Date.now() / 1000);
            this.countdownEndTime = now + (timerMinutes * 60);
            localStorage.setItem(this.countdownKey, String(this.countdownEndTime));
        }
    }

    buildGroupedSheetDetailsTable(): any[] {
        const pivotIds = this.cases?.[0]?.pivot?.map((p: any) => p.sheet_detail_id) || [];
        const groups: {
            [key: string]: {
                sheet_name: string,
                header: string,
                details: any[],
                interpretasi: number
            }
        } = {};

        for (const detail of this.sheetDetailsList) {
            const key = `${detail.sheet_name}||${detail.header}`;
            if (!groups[key]) {
                groups[key] = {
                    sheet_name: detail.sheet_name,
                    header: detail.header,
                    details: [],
                    interpretasi: 0
                };
            }
            groups[key].details.push(detail);
            // Cek apakah ID detail ini ada di pivot
            if (pivotIds.includes(detail.id)) {
                groups[key].interpretasi = 1;
            }
        }

        return Object.values(groups);
    }

    // Method untuk memeriksa apakah semua input yang diperlukan telah diisi
    isTabComplete(tabIndex: number): boolean {
        switch (tabIndex) {
            case 0: // Pemeriksaan
                // Periksa apakah minimal 1 pemeriksaan dipilih
                const hasSelectedExamination = Object.values(this.checkedSheetDetails).some(v => v);
                if (!hasSelectedExamination) return false;

                // Periksa apakah semua textarea interpretasi yang diperlukan telah diisi
                for (const group of this.groupedSheetDetailsTableResult) {
                    if (group.interpretasi === 1) {
                        const key = group.sheet_name + '||' + group.header;
                        const interpretation = this.interpretasiForm[key];
                        if (!interpretation || interpretation.trim().length === 0) {
                            return false;
                        }
                    }
                }
                return true;

            case 1: // Diagnosis
                // Periksa apakah semua diagnosa telah diisi (utama, banding 1, dan banding 2)
                return Boolean(this.diagnosaForm.diagnosaUtama && this.diagnosaForm.diagnosaUtama.trim().length > 0) &&
                       Boolean(this.diagnosaForm.diagnosaBanding1 && this.diagnosaForm.diagnosaBanding1.trim().length > 0) &&
                       Boolean(this.diagnosaForm.diagnosaBanding2 && this.diagnosaForm.diagnosaBanding2.trim().length > 0);

            case 2: // Terapi
                // Periksa apakah ada minimal 1 resep yang valid
                return this.hasValidResep;

            default:
                return false;
        }
    }

    // Method untuk memeriksa apakah ada sheet detail yang dipilih
    hasCheckedSheetDetails(): boolean {
        return Object.values(this.checkedSheetDetails).some(checked => checked);
    }

    // Method untuk mendapatkan status kelengkapan tab saat ini
    isCurrentTabComplete(): boolean {
        return this.isTabComplete(this.activeTab);
    }

    // Method untuk mendapatkan detail yang memerlukan interpretasi
    getDetailsForInterpretation(details: any[]): any[] {
        const pivotIds = this.cases?.[0]?.pivot?.map((p: any) => p.sheet_detail_id) || [];
        return details.filter(detail => pivotIds.includes(detail.id));
    }

    // Tambahkan fungsi addResep agar bisa dipanggil dari template
    addResep = (): void => {
        this.resepList.push('');
        this.saveResepData();
    }

    // Tambahkan fungsi removeResep agar bisa dipanggil dari template
    removeResep = (index: number): void => {
        if (this.resepList.length > 1) {
            this.resepList.splice(index, 1);
            this.saveResepData();
        }
    }

    // Fungsi untuk reset pilihan pemeriksaan dan kembali ke mode checklist
    resetPemeriksaanSelection(): void {
        // Clear sheet details list untuk menampilkan kembali checklist
        this.sheetDetailsList = [];
        this.groupedSheetDetailsTableResult = [];
        
        // Reset interpretasi form untuk OSCE saat ini
        this.interpretasiForm = {};
        
        // Hapus data tersimpan di localStorage
        localStorage.removeItem(`interpretasi_${this.caseId}_${this.currentOsceIndex}`);
        
        // Tetap pertahankan checkbox yang sudah dipilih
        // checkedSheetDetails dan checkedSheetDetailsNames tidak direset
        
        // Terapkan ulang filter pencarian
        this.applySearchSheetDetail();
        
        // Tampilkan pesan konfirmasi
        this.messageService.add({
            severity: 'info',
            summary: 'Pemeriksaan Direset',
            detail: 'Anda dapat memilih ulang pemeriksaan yang diinginkan.'
        });
    }

    // Method untuk mendapatkan warna card berdasarkan index
    getCardColor(index: number): string {
        const colors = [
            'bg-red-100 border-red-200 hover:bg-red-200',
            'bg-orange-100 border-orange-200 hover:bg-orange-200',
            'bg-green-100 border-green-200 hover:bg-green-200',
            'bg-blue-100 border-blue-200 hover:bg-blue-200',
            'bg-purple-100 border-purple-200 hover:bg-purple-200',
            'bg-pink-100 border-pink-200 hover:bg-pink-200'
        ];
        return colors[index % colors.length];
    }
}
